import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../../utils/apiConfig';
import { showError } from '../../components/CustomAlert';
import { FaChevronDown, FaChevronRight, FaSearch, FaTimes } from 'react-icons/fa';
import { format, addDays } from 'date-fns';
import { useParams, useSearchParams } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { hasPermission } from '../../utils/permissions';
const Container = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
`;

const FilterContainer = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: end;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
  
  &::after {
    content: "*";
    color: #dc3545;
    margin-left: 4px;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
  
  option {
    color: #333;
  }
`;

const SearchButton = styled.button`
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: transform 0.2s ease;
  background: transparent;
  border: 1px solid #e9ecef;
  color: #2f2e2e;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const ContentContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoRecordsMessage = styled.div`
  color: #6c757d;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #333;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  color: #333;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'Present') return '#d4edda';
    if (props.status === 'Absent') return '#f8d7da';
    if (props.status === 'On Leave') return '#fff3cd';
    if (props.status === 'Others') return '#d1ecf1';
    return '#e2e3e5';
  }};
  color: ${props => {
    if (props.status === 'Present') return '#155724';
    if (props.status === 'Absent') return '#721c24';
    if (props.status === 'On Leave') return '#856404';
    if (props.status === 'Others') return '#0c5460';
    return '#6c757d';
  }};
`;

const AddAttendanceSheetButton = styled.button`
  background: #1c7ab9;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #07306c;
    color: white;
  }
`;

const DownloadButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  
  &:hover {
    background: #218838;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 5px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
`;

const ExpandedRow = styled.tr`
  background: #f8f9fa;
`;

const ExpandedCell = styled.td`
  padding: 20px;
  border-top: 1px solid #e9ecef;
`;

const WeekGrid = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const WeekTh = styled.th`
  padding: 8px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  text-align: center;
`;

const WeekTd = styled.td`
  padding: 8px;
  border: 1px solid #dee2e6;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #007bff;
    color: #007bff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 20px;
`;

const SearchInput = styled.input`
  height: 48px;
  padding: 12px 45px 12px 45px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  pointer-events: none;
`;

const ClearSearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #6c757d;
  }
`;

const AttendanceReport = () => {
  const { user } = useSelector(state => state.auth);
  const canCreateAttendance = hasPermission(user, 'attendance_create');

  const [filters, setFilters] = useState({
    year: new Date().getFullYear().toString(),
    course: '',
    session: ''
  });
  const { courses: masterCourses, sessions: masterSessions } = useSelector(state => state.masterData);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [trainingPeriodByStudent, setTrainingPeriodByStudent] = useState({}); // { [id]: { start, end } | null }
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allAttendanceData, setAllAttendanceData] = useState([]);
  
  // Filter sessions by selected course
  const filteredSessions = filters.course 
    ? masterSessions.filter(session => session.course_id == filters.course || session.course_id == parseInt(filters.course))
    : [];
  
  // No month UI anymore

  // Generate years based on selected session date to current year
  const currentYear = new Date().getFullYear();
  const getYearOptions = () => {
    if (!selectedSession || !selectedSession.session_date) {
      return Array.from({ length: 6 }, (_, i) => (currentYear - i).toString());
    }

    const sessionYear = new Date(selectedSession.session_date).getFullYear();
    const years = [];
    for (let year = sessionYear; year <= currentYear; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset session when course changes
    if (field === 'course') {
      setFilters(prev => ({
        ...prev,
        session: ''
      }));
      setSelectedSession(null);
    }

    // Update selectedSession when session changes
    if (field === 'session') {
      const session = masterSessions.find(s => s.id.toString() === value);
      setSelectedSession(session || null);
    }
  };

  const fetchStudents = async (page = 1, useFilters = false) => {
    setLoading(true);
    try {
      let url;
      if (useFilters && filters.session) {
        // Filtered search - full session roster (name/email search is client-side)
        url = `${API_BASE_URL}/attendance/students?sessionId=${filters.session}`;
      } else {
        // Unfiltered: paginated list; search is applied on the server across all matching students
        const params = new URLSearchParams({
          page: String(page),
          limit: '20',
        });
        const q = searchTerm.trim();
        if (q) params.set('search', q);
        url = `${API_BASE_URL}/attendance/students?${params}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      if (data?.success && data?.students) {
        const mapped = data.students.map((s) => ({
          studentId: s.id || s.student_id || s.studentId,
          studentName: `${s.first_name || s.firstName || ''} ${s.last_name || s.lastName || ''}`.trim(),
          email: s.email || '',
          registrationNumber: s.registration_number || ''
        }));
        setAllAttendanceData(mapped);
        // The useEffect hook will handle filtering based on searchTerm
        
        // Update pagination if available (only for unfiltered searches)
        if (data.pagination) {
          setPagination({
            page: data.pagination.page,
            limit: data.pagination.limit,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages
          });
        } else {
          // For filtered searches, reset pagination
          setPagination({
            page: 1,
            limit: 20,
            total: mapped.length,
            totalPages: 1
          });
        }
      } else {
        setAttendanceData([]);
        setAllAttendanceData([]);
        setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      showError('Failed to load students');
      setAttendanceData([]);
      setAllAttendanceData([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Filter attendance data on the client only when the full session list is already loaded (server search handles the global paginated list)
  useEffect(() => {
    if (!isFiltered) {
      setAttendanceData(allAttendanceData);
      return;
    }
    if (!searchTerm.trim()) {
      setAttendanceData(allAttendanceData);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = allAttendanceData.filter(student => (
      student.studentName?.toLowerCase().includes(searchLower) ||
      student.email?.toLowerCase().includes(searchLower) ||
      student.registrationNumber?.toLowerCase().includes(searchLower)
    ));
    setAttendanceData(filtered);
  }, [searchTerm, allAttendanceData, isFiltered]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSearch = async () => {
    if (!filters.year || !filters.course || !filters.session) {
      showError('Please select Year, Course, and Session');
      return;
    }
    setIsFiltered(true);
    await fetchStudents(1, true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchStudents(newPage, false);
    }
  };

  const toggleExpandedRow = (studentId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Helper to get week number in month (1-based)
  function getWeekOfMonth(date) {
    const day = date.getDate();
    // getDay() returns 0 (Sunday) to 6 (Saturday)
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayWeekday = firstDayOfMonth.getDay();
    // Calculate offset: if first day is not Sunday, week starts from that day
    return Math.floor((day + firstDayWeekday - 1) / 7) + 1;
  }

  // Helper to get the total number of weeks in a month
  function getWeeksInMonth(year, month) {
    const monthNum = getMonthNumber(month);
    if (!year || !monthNum) return 4;
    
    // Get first and last day of the month
    const firstDay = new Date(year, monthNum - 1, 1);
    const lastDay = new Date(year, monthNum, 0);
    
    // Calculate weeks that contain days in this month
    const firstWeek = getWeekOfMonth(firstDay);
    const lastWeek = getWeekOfMonth(lastDay);
    
    // Return the number of weeks (should be 4-5 for most months)
    return Math.min(lastWeek - firstWeek + 1, 5);
  }

  // Helper to convert month name to number
  function getMonthNumber(monthStr) {
    if (!monthStr) return null;
    const monthNum = parseInt(monthStr);
    if (!isNaN(monthNum)) return monthNum;
    
    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const monthIndex = monthNames.indexOf(monthStr.toLowerCase());
    return monthIndex !== -1 ? monthIndex + 1 : null;
  }

  const { studentId } = useParams();
  const [searchParams] = useSearchParams();

  const year = searchParams.get('year');

  // Global list: load when course/session are cleared; debounce search so the query runs across all students, not only the current page
  useEffect(() => {
    if (filters.course || filters.session) return;
    setIsFiltered(false);
    const delayMs = searchTerm.trim() ? 300 : 0;
    const id = setTimeout(() => {
      fetchStudents(1, false);
    }, delayMs);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.course, filters.session, searchTerm]);

  const handleOpenAttendanceSheet = (studentId, start, end) => {
    const sessionId = filters.session || (selectedSession && selectedSession.id);
    const url = `/admin/students/${studentId}/attendance-sheet?start=${start}&end=${end}${sessionId ? `&sessionId=${sessionId}` : ''}`;
    window.open(url, '_blank');
  };

  const handleOpenAddAttendanceSheet = (studentId) => {
    const sessionId = filters.session || (selectedSession && selectedSession.id);
    const url = `/admin/students/${studentId}/attendance-sheet${sessionId ? `?sessionId=${sessionId}` : ''}`;
    window.open(url, '_blank');
  };

  // Compute 4-week ranges from a start date to today
  const buildFourWeekRanges = (startDate, hardEndDate) => {
    const ranges = [];
    let cursor = new Date(startDate);
    const today = new Date();
    // Always cap at today to avoid generating future ranges
    const providedEnd = hardEndDate ? new Date(hardEndDate) : null;
    const maxEnd = providedEnd && providedEnd < today ? providedEnd : today;
    while (cursor <= maxEnd) {
      // Compute 4-week end aligned with Friday-only weeks
      const startDow = cursor.getDay(); // 0 Sun .. 6 Sat
      const firstWeekEndOffset = (5 - startDow + 7) % 7; // days until Friday
      const firstWeekEnd = addDays(cursor, firstWeekEndOffset);
      const firstMondayAfter = addDays(firstWeekEnd, 3);
      const fourthFriday = addDays(firstMondayAfter, 18); // Monday + 18 days = 4th Friday
      const naturalEnd = fourthFriday;
      const end = new Date(Math.min(naturalEnd.getTime(), maxEnd.getTime()));
      const labelStart = format(cursor, 'MM/dd/yy');
      const labelEnd = format(end, 'MM/dd/yy');
      const urlStart = format(cursor, 'yyyy-MM-dd');
      const urlEnd = format(end, 'yyyy-MM-dd');
      ranges.push({ labelStart, labelEnd, urlStart, urlEnd });
      cursor = addDays(end, 1);
    }
    return ranges;
  };

  const ensureTrainingStart = async (studentId) => {
    if (trainingPeriodByStudent[studentId]) return trainingPeriodByStudent[studentId];
    try {
      const res = await fetch(`${API_BASE_URL}/attendance/sheet-data/${studentId}`);
      const json = await res.json();
      const start = json?.data?.voucher?.training_period_start_date;
      const end = json?.data?.voucher?.training_period_end_date;
      if (start) {
        const period = { start, end: end || null };
        setTrainingPeriodByStudent(prev => ({ ...prev, [studentId]: period }));
        return period;
      }
      // Cache explicit null when not found to avoid perpetual loading UI
      setTrainingPeriodByStudent(prev => ({ ...prev, [studentId]: null }));
    } catch (_) {}
    return null;
  };

  const shouldShowPagination = !isFiltered && pagination.totalPages > 1;

  return (
    <Container>
      <Title>Attendance Report</Title>

      <FilterContainer>
        <FilterRow>


          <FilterGroup>
            <Label>Course</Label>
            <Select
              value={filters.course}
              onChange={(e) => handleFilterChange('course', e.target.value)}
            >
              <option value="">--Select--</option>
              {masterCourses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.course_name} ({course.course_code})
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Session</Label>
            <Select
              value={filters.session}
              onChange={(e) => handleFilterChange('session', e.target.value)}
              disabled={!filters.course}
            >
              <option value="">{filters.course ? '--Select--' : 'Select a course first'}</option>
              {filteredSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.session_name || `Session ${session.id}`}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Year</Label>
            <Select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
            >
              <option value="">--Select--</option>
              {getYearOptions().map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Select>
          </FilterGroup>

          {/* Month filter removed */}

          <SearchButton onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </SearchButton>
        </FilterRow>
        
        {/* Search by name, email, and registration number */}
        <SearchInputContainer>
          <SearchIcon><FaSearch /></SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search by student name, email, or registration number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearSearchIcon onClick={handleClearSearch}>
              <FaTimes />
            </ClearSearchIcon>
          )}
        </SearchInputContainer>
      </FilterContainer>

      <ContentContainer>
        {loading ? (
          <LoadingSpinner>Loading attendance data...</LoadingSpinner>
        ) : attendanceData.length > 0 ? (
          <div style={{ padding: '20px', width: '100%', overflowX: 'auto' }}>
            <Table style={{display: 'inline-table'}}>
              <thead>
                <tr>
                  <Th>All Attendance Sheets</Th>
                  <Th>Student</Th>
                  <Th>Latest Monthly Attendance Sheet</Th>
                  <Th></Th>
                </tr>

              </thead>
              <tbody>
                {attendanceData.map((student) => {
                  const hasAttendanceRecords = true; // Always allow expand for links
                  
                  return (
                    <React.Fragment key={student.studentId}>
                      <tr>
                        <Td>
                          {hasAttendanceRecords ? (
                            <ExpandButton onClick={() => toggleExpandedRow(student.studentId)}>
                              {expandedRows.has(student.studentId) ? <FaChevronDown /> : <FaChevronRight />}
                            </ExpandButton>
                          ) : null}
                        </Td>
                        <Td>
                          {student.studentName}
                        </Td>
                        <Td>
                          {(() => {
                            const period = trainingPeriodByStudent[student.studentId];
                            if (typeof period === 'undefined') {
                              // Trigger fetch silently on first render
                              ensureTrainingStart(student.studentId);
                              return <span style={{ color: '#6c757d' }}>Loading...</span>;
                            }
                            if (period === null || !period.start) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                                  {canCreateAttendance && (
                                    <AddAttendanceSheetButton onClick={() => handleOpenAddAttendanceSheet(student.studentId)} >
                                      <><FaPlus style={{ marginRight: 14 }} size={12} />Add attendance</>
                                    </AddAttendanceSheetButton>
                                  )}
                                  <span style={{ color: '#6c757d' }}>No attendance record</span>
                                </div>
                              );
                            }
                            const ranges = buildFourWeekRanges(new Date(period.start), period.end || null);
                            if (ranges.length === 0) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                                  {canCreateAttendance && (
                                    <AddAttendanceSheetButton onClick={() => handleOpenAddAttendanceSheet(student.studentId)}>
                                      Add attendance
                                    </AddAttendanceSheetButton>
                                  )}
                                  <span style={{ color: '#6c757d' }}>No attendance record</span>
                                </div>
                              );
                            }
                            const latest = ranges[ranges.length - 1];
                            return (
                              <DownloadButton
                                onClick={() => {
                                  handleOpenAttendanceSheet(student.studentId, latest.urlStart, latest.urlEnd);
                                }}
                              >
                                Latest monthly attendance sheet
                              </DownloadButton>
                            );
                          })()}
                        </Td>
                      </tr>
                    {expandedRows.has(student.studentId) && (
                      <ExpandedRow>
                        <ExpandedCell colSpan={3}>
                          <div>
                            {async function RenderLinks() { return null; }}
                            {(() => {
                              const period = trainingPeriodByStudent[student.studentId];
                              if (typeof period === 'undefined') {
                                // Trigger fetch silently on first render
                                ensureTrainingStart(student.studentId);
                                return <div style={{color:'#6c757d'}}>Loading sheets...</div>;
                              }
                              if (period === null) {
                                return <div style={{color:'#6c757d'}}>Please upload voucher for the student and there attendance record to see their attendance sheets.</div>;
                              }
                              const ranges = buildFourWeekRanges(new Date(period.start), period.end || null);
                              return (
                                <div>
                                  {ranges.map(r => (
                                    <a key={r.urlStart}
                                       onClick={() => handleOpenAttendanceSheet(student.studentId, r.urlStart, r.urlEnd)}
                                       style={{ color: '#0d6efd', display: 'block', marginBottom: 8, cursor: 'pointer' }}>
                                      {`Attendance sheet for ${r.labelStart} to ${r.labelEnd}`}
                                    </a>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </ExpandedCell>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                );
              })}
              </tbody>
          </Table>
          {shouldShowPagination && (
            <PaginationContainer>
              <PaginationInfo>
                Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </PaginationInfo>
              <PaginationButtons>
                <PageButton
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages || loading}
                >
                  Next
                </PageButton>
              </PaginationButtons>
            </PaginationContainer>
          )}
          </div>
        ) : (
          <NoRecordsMessage>
            No Records found
          </NoRecordsMessage>
        )}
      </ContentContainer>
    </Container>
  );
};

export default AttendanceReport; 